import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { formatApiError } from "../lib/api";
import { Logo } from "../components/site/Logo";
import { Check } from "lucide-react";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ email: "", password: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      await login(form.email, form.password);
      toast.success("Accesso effettuato");
      navigate("/area");
    } catch (err) {
      const msg = formatApiError(err.response?.data?.detail) || "Errore di accesso";
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen bg-white" data-testid="login-page">
      <div className="hidden w-1/2 flex-col justify-between bg-[#0A192F] p-12 lg:flex">
        <Logo dark />
        <div>
          <h2 className="font-display text-3xl font-bold leading-tight text-white">Bentornato nella tua Area Clienti</h2>
          <ul className="mt-8 space-y-3">
            {["Gestisci le tue pratiche", "Carica e consulta i documenti", "Scrivi al nostro team"].map((f) => (
              <li key={f} className="flex items-center gap-3 text-slate-300">
                <Check className="h-5 w-5 text-[#10B981]" /> {f}
              </li>
            ))}
          </ul>
        </div>
        <p className="text-sm text-slate-500">© {new Date().getFullYear()} ViviTranquillo</p>
      </div>

      <div className="flex w-full items-center justify-center px-6 lg:w-1/2">
        <div className="w-full max-w-md">
          <div className="lg:hidden"><Logo /></div>
          <h1 className="mt-8 font-display text-3xl font-bold text-[#0A192F]">Accedi</h1>
          <p className="mt-2 text-slate-600">Entra nella tua Area Clienti ViviTranquillo.</p>

          {error && <div data-testid="login-error" className="mt-6 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>}

          <form onSubmit={submit} className="mt-6 space-y-4">
            <div>
              <label className="mb-2 block text-sm font-semibold text-[#0A192F]">Email</label>
              <input data-testid="login-email" required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="w-full rounded-xl border border-slate-200 px-4 py-3 outline-none focus:ring-2 focus:ring-[#10B981]" />
            </div>
            <div>
              <div className="mb-2 flex items-center justify-between">
                <label className="block text-sm font-semibold text-[#0A192F]">Password</label>
                <Link to="/password-dimenticata" data-testid="login-forgot-link" className="text-sm font-semibold text-[#10B981] hover:text-[#059669]">
                  Password dimenticata?
                </Link>
              </div>
              <input data-testid="login-password" required type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="w-full rounded-xl border border-slate-200 px-4 py-3 outline-none focus:ring-2 focus:ring-[#10B981]" />
            </div>
            <button data-testid="login-submit" disabled={loading} className="w-full rounded-full bg-[#10B981] px-8 py-3.5 font-semibold text-white transition-colors hover:bg-[#059669] disabled:opacity-60">
              {loading ? "Accesso..." : "Accedi"}
            </button>
          </form>

          <p className="mt-6 text-center text-sm text-slate-600">
            Non hai un account?{" "}
            <Link to="/registrazione" data-testid="login-to-register" className="font-semibold text-[#10B981]">Registrati</Link>
          </p>
          <p className="mt-2 text-center text-sm">
            <Link to="/" className="text-slate-400 hover:text-[#0A192F]">← Torna alla home</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
